import { Container, createStyles } from '@mantine/core';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { useRequestAccess } from '../../api/hooks/notification/useRequestAccess';
import type { ButtonDetails } from '../EmptyState';

import { EmptyState } from '../EmptyState';
import { getIdFromURL } from './utils';

const useStyles = createStyles((theme) => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: theme.other.space[80],
		gap: theme.other.space[4],
		paddingRight: 0,
		paddingLeft: 0,
	},
	box: {
		padding: theme.other.space[5],
		background: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.other.borderRadius.md,
		display: 'flex',
	},
	infoCircleIcon: {
		marginRight: theme.other.space[1],
	},
	helperText: {
		fontSize: theme.fontSizes.md,
		color: theme.other.getColor('text/secondary/default'),
	},
}));

function RequestAccess({ entityId }: { entityId: string }): JSX.Element {
	const [hasRequestedAccess, setHasRequestedAccess] = useState(false);
	const { isPending: isRequestingAccessing, mutate: requestAccess } =
		useRequestAccess({
			entityId,
		});

	const handleRequestAccess = () => {
		requestAccess(undefined, {
			onSuccess: () => {
				setHasRequestedAccess(true);
			},
		});
	};

	const buttons: ButtonDetails[] = [
		{
			action: handleRequestAccess,
			isPrimary: true,
			name: 'Request access',
			size: 'md',
			loading: isRequestingAccessing,
			disabled: hasRequestedAccess,
		},
	];

	const { classes } = useStyles();

	return (
		<Container className={classes.container} data-testid="request-access">
			<EmptyState
				title="No access to this resource"
				description="An admin on your team can grant you access to this resource"
				buttons={buttons}
				includeGoBack
				iconName="lock"
				size="lg"
			/>
		</Container>
	);
}

function Error403(): JSX.Element {
	const location = useLocation();
	const { classes } = useStyles();

	const entityId = getIdFromURL(location.pathname);
	if (entityId) {
		return <RequestAccess entityId={entityId} />;
	}
	return (
		<Container className={classes.container} data-testid="error-403">
			<EmptyState
				title="No permissions for this resource"
				description="An admin in your workspace can grant you access to this resource"
				iconName="lock"
				size="lg"
				includeGoBack
			/>
		</Container>
	);
}

export default Error403;
